/**
 * Page back-office : messagerie parents ↔ école.
 * Liste à gauche, conversation à droite (Master-Detail).
 */
import React, { useState, useMemo } from 'react';
import {
  useGetMessagesParentsQuery,
  useGetThreadParentQuery,
  useRepondreThreadParentMutation,
  useArchiverThreadParentMutation,
} from '../../services/messagesParentsApi';
import {
  Inbox, Phone, Mail, GraduationCap, ShieldCheck, BookOpen,
  Archive, ArchiveRestore, Send, Search,
} from 'lucide-react';

const TYPE_META: Record<string, { label: string; couleur: string; bgLight: string; bgDark: string; Icon: any }> = {
  scolarite:   { label: 'Scolarité',   couleur: '#2563eb', bgLight: 'bg-blue-50',   bgDark: 'dark:bg-blue-900/30',   Icon: GraduationCap },
  surveillant: { label: 'Surveillant', couleur: '#16a34a', bgLight: 'bg-green-50',  bgDark: 'dark:bg-green-900/30',  Icon: ShieldCheck },
  formateur:   { label: 'Formateur',   couleur: '#7c3aed', bgLight: 'bg-purple-50', bgDark: 'dark:bg-purple-900/30', Icon: BookOpen },
};

export default function MessagesParents() {
  const [statut, setStatut] = useState<'tous' | 'non_lus' | 'archives'>('tous');
  const [typeFiltre, setTypeFiltre] = useState<'' | 'scolarite' | 'surveillant' | 'formateur'>('');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data, isFetching, refetch } = useGetMessagesParentsQuery({
    statut, type: typeFiltre || undefined, q: q || undefined,
  });

  const threads = useMemo(() => data?.results || [], [data]);
  const rolesVisibles = data?.roles_visibles || [];

  return (
    <div className="flex h-[calc(100vh-72px)] bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Colonne gauche : liste */}
      <aside className="w-96 border-r border-gray-200 dark:border-gray-800 flex flex-col">
        <header className="p-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-bold flex items-center gap-2 text-gray-900 dark:text-white">
              <Inbox size={18} /> Messages parents
              {(data?.nb_total_non_lus ?? 0) > 0 && (
                <span className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                  {data?.nb_total_non_lus}
                </span>
              )}
            </h1>
            <button onClick={() => refetch()}
              className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
              {isFetching ? '…' : '↻'}
            </button>
          </div>

          <div className="relative mb-2">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text" placeholder="Rechercher…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="w-full pl-7 pr-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex gap-1 mb-1.5">
            {(['tous', 'non_lus', 'archives'] as const).map(s => (
              <button key={s} onClick={() => setStatut(s)}
                className={`flex-1 px-2 py-1 text-[11px] rounded ${
                  statut === s
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}>
                {s === 'tous' ? 'Tous' : s === 'non_lus' ? 'Non lus' : 'Archivés'}
              </button>
            ))}
          </div>

          {rolesVisibles.length > 1 && (
            <div className="flex gap-1 overflow-x-auto">
              <button onClick={() => setTypeFiltre('')}
                className={`px-2 py-1 text-[10px] rounded whitespace-nowrap ${
                  !typeFiltre
                    ? 'bg-gray-700 dark:bg-gray-700 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}>
                Toutes les boîtes
              </button>
              {rolesVisibles.map(r => {
                const m = TYPE_META[r];
                if (!m) return null;
                return (
                  <button key={r} onClick={() => setTypeFiltre(r as any)}
                    className={`px-2 py-1 text-[10px] rounded whitespace-nowrap ${
                      typeFiltre === r
                        ? 'bg-gray-700 dark:bg-gray-700 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}>
                    {m.label}
                  </button>
                );
              })}
            </div>
          )}
        </header>

        <div className="flex-1 overflow-y-auto">
          {isFetching && threads.length === 0 && (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">Chargement…</div>
          )}
          {!isFetching && threads.length === 0 && (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">Aucun message.</div>
          )}
          {threads.map(t => {
            const m = TYPE_META[t.destinataire_type] || TYPE_META.scolarite;
            const lu = t.nb_non_lus === 0;
            return (
              <button key={t.id} onClick={() => setSelectedId(t.id)}
                className={`w-full text-left p-3 border-b border-gray-100 dark:border-gray-800/50 transition ${
                  selectedId === t.id
                    ? 'bg-blue-50 dark:bg-blue-900/30'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {(t.parent_prenoms?.[0] || '') + (t.parent_nom?.[0] || '')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm truncate ${
                        lu
                          ? 'text-gray-700 dark:text-gray-300'
                          : 'text-gray-900 dark:text-white font-semibold'
                      }`}>
                        {t.parent_prenoms} {t.parent_nom}
                      </span>
                      <span className="text-[10px] text-gray-500 flex-shrink-0">
                        {new Date(t.dernier_date || t.date_envoi).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <m.Icon size={10} style={{ color: m.couleur }} />
                      <span className="text-[10px] truncate" style={{ color: m.couleur }}>
                        {m.label}{t.matiere_libelle ? ` • ${t.matiere_libelle}` : ''}
                      </span>
                    </div>
                    <div className={`text-xs mt-1 truncate ${
                      lu ? 'text-gray-500 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {t.sujet || '(sans sujet)'}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-500 truncate mt-0.5">
                      {t.dernier_auteur === 'ecole' && <span className="text-blue-500 dark:text-blue-400">↪ </span>}
                      {t.extrait_dernier}
                    </div>
                  </div>
                  {(t.nb_non_lus ?? 0) > 0 && (
                    <div className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-blue-500 text-white rounded-full px-1">
                      {t.nb_non_lus}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Colonne droite : détail */}
      <section className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-950">
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500">
            <div className="text-center">
              <Inbox size={48} strokeWidth={1.2} className="mx-auto mb-3 opacity-30" />
              <div>Sélectionnez une conversation</div>
            </div>
          </div>
        ) : (
          <ThreadView id={selectedId} onArchive={() => setSelectedId(null)} />
        )}
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function ThreadView({ id, onArchive }: { id: number; onArchive: () => void }) {
  const { data, isFetching } = useGetThreadParentQuery(id);
  const [repondre, { isLoading: sending }] = useRepondreThreadParentMutation();
  const [archiver] = useArchiverThreadParentMutation();
  const [reponse, setReponse] = useState('');

  if (isFetching || !data) {
    return <div className="flex-1 flex items-center justify-center text-gray-500">Chargement…</div>;
  }

  const t = data.racine;
  const meta = TYPE_META[t.destinataire_type] || TYPE_META.scolarite;

  const handleEnvoyer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reponse.trim()) return;
    try {
      await repondre({ id, contenu: reponse }).unwrap();
      setReponse('');
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur d\'envoi.');
    }
  };

  const handleArchiver = async () => {
    if (!window.confirm(t.archive ? 'Désarchiver ce thread ?' : 'Archiver ce thread ?')) return;
    await archiver({ id, archive: !t.archive });
    onArchive();
  };

  return (
    <>
      {/* En-tête conversation */}
      <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-start gap-4 bg-white dark:bg-gray-900">
        <div className="w-11 h-11 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
          {(t.parent_prenoms?.[0] || '') + (t.parent_nom?.[0] || '')}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {t.parent_prenoms} {t.parent_nom}
            </h2>
            <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full ${meta.bgLight} ${meta.bgDark}`}
                  style={{ color: meta.couleur }}>
              <meta.Icon size={11} /> {meta.label}
            </span>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-3 flex-wrap">
            {t.parent_telephone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {t.parent_telephone}</span>}
            {t.parent_email && <span className="inline-flex items-center gap-1"><Mail size={10} /> {t.parent_email}</span>}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Concerne : <span className="text-gray-800 dark:text-gray-200">{data.enfant.prenoms} {data.enfant.nom}</span>{' '}
            ({data.enfant.classe} — {t.matricule_etudiant})
          </div>
          {t.matiere_libelle && (
            <div className="text-xs text-purple-600 dark:text-purple-300 mt-1">Matière : {t.matiere_libelle}</div>
          )}
        </div>
        <button onClick={handleArchiver}
          className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded flex items-center gap-1.5">
          {t.archive ? <><ArchiveRestore size={13} /> Désarchiver</> : <><Archive size={13} /> Archiver</>}
        </button>
      </header>

      <div className="px-5 py-2 text-xs text-gray-600 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        Sujet : <span className="text-gray-900 dark:text-gray-200 font-medium">{t.sujet || '(sans sujet)'}</span>
      </div>

      {/* Fil des messages */}
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {data.messages.map(m => {
          const estParent = m.auteur_type === 'parent';
          return (
            <div key={m.id} className={`flex ${estParent ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[75%] p-3 rounded-xl ${
                estParent
                  ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                  : 'bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-700'
              }`}>
                <div className="text-[10px] font-semibold mb-1"
                     style={{ color: estParent ? undefined : '#2563eb' }}>
                  <span className={estParent ? 'text-gray-500 dark:text-gray-400' : ''}>
                    {m.auteur_nom_affiche || (estParent ? 'Parent' : 'École')}
                  </span>
                </div>
                <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{m.contenu}</div>
                <div className="text-[10px] text-gray-500 mt-1 text-right">
                  {new Date(m.date_envoi).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <form onSubmit={handleEnvoyer}
            className="border-t border-gray-200 dark:border-gray-800 p-3 flex gap-2 bg-white dark:bg-gray-900">
        <textarea
          value={reponse} onChange={(e) => setReponse(e.target.value)}
          placeholder="Votre réponse…" rows={2}
          className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 resize-none"
          maxLength={5000}
        />
        <button type="submit" disabled={sending || !reponse.trim()}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded inline-flex items-center gap-2 self-end">
          <Send size={14} /> {sending ? '…' : 'Envoyer'}
        </button>
      </form>
    </>
  );
}
